import { inject } from '@angular/core';
import { ResolveFn, Routes } from '@angular/router';

import { branchManagementEditGuard } from '../../core/guards/branch-management-edit.guard';
import { provideClientManagementFeature } from './client-management.module';
import { ClientManagementService } from '../../core/services/client-management.service';
import { ClientRead } from './models/client-management';
import { hierarchySetupCanDeactivateGuard } from './hierarchy/guards/hierarchy-setup-can-deactivate.guard';

const branchManagementClientResolver: ResolveFn<ClientRead> = (route) =>
  inject(ClientManagementService).getMyClientBranches(route.paramMap.get('clientId') ?? '');

export const CLIENT_MANAGEMENT_ROUTES: Routes = [
  {
    path: 'client-management/branch-management/:clientId/edit',
    canActivate: [branchManagementEditGuard],
    providers: [provideClientManagementFeature()],
    loadComponent: () =>
      import('./pages/branch-management-edit/branch-management-edit').then(
        (m) => m.BranchManagementEdit,
      ),
    data: {
      formMode: 'edit',
      layout: {
        activeItem: 'Branch Management',
        title: 'Edit Client',
        subtitle: 'Update client details',
        backTo: '/client-management/branch-management',
        showToolbar: false,
        showBackButton: false,
      },
    },
  },
  {
    path: 'client-management/branch-management/:clientId/view',
    providers: [provideClientManagementFeature()],
    loadComponent: () =>
      import('./pages/branch-management-edit/branch-management-edit').then(
        (m) => m.BranchManagementEdit,
      ),
    data: {
      formMode: 'edit',
      readOnly: true,
      layout: {
        activeItem: 'Branch Management',
        title: 'View Branch',
        subtitle: 'Review branch details',
        backTo: '/client-management/branch-management',
        showToolbar: false,
        showBackButton: false,
      },
    },
  },
  {
    path: 'client-management/branch-management/:clientId',
    providers: [provideClientManagementFeature()],
    loadComponent: () =>
      import('./pages/branch-management/branch-management').then((m) => m.BranchManagement),
    data: {
      layout: {
        activeItem: 'Branch Management',
        title: '',
        subtitle: 'View and update client details',
        showToolbar: false,
      },
    },
    resolve: { client: branchManagementClientResolver },
  },
  {
    path: 'client-management/create',
    providers: [provideClientManagementFeature()],
    loadComponent: () => import('./pages/create-client/create-client').then((m) => m.CreateClient),
    data: {
      formMode: 'create',
      layout: {
        activeItem: 'Client Management',
        breadcrumb: 'Client Management > Add New Client',
        title: 'Add New Client',
        subtitle: 'Setup new client',
        showToolbar: false,
        backTo: '/client-management',
      },
    },
  },
  {
    path: 'client-management/:clientId/hierarchy',
    canDeactivate: [hierarchySetupCanDeactivateGuard],
    loadComponent: () =>
      import('./hierarchy/pages/hierarchy-setup/hierarchy-setup').then((m) => m.HierarchySetup),
    data: {
      layout: {
        activeItem: 'Client Management',
        breadcrumb: 'Client Management > Client Hierarchy',
        title: 'Client Hierarchy',
        subtitle:
          "Configure this client's hierarchy levels, location groups, branch coverage and approval routing.",
        showToolbar: false,
        backTo: '/client-management',
      },
    },
  },
  {
    path: 'client-management/:clientId/hierarchy/details',
    loadComponent: () =>
      import('./hierarchy/pages/hierarchy-view/hierarchy-view').then((m) => m.HierarchyView),
    data: {
      layout: {
        activeItem: 'Client Management',
        breadcrumb: 'Client Management > Client Hierarchy',
        title: 'Client Hierarchy',
        subtitle: 'Configured roles by hierarchy level',
        showToolbar: false,
        backTo: '/client-management',
      },
    },
  },
  {
    path: 'client-management/:clientId/edit',
    providers: [provideClientManagementFeature()],
    loadComponent: () => import('./pages/edit-client/edit-client').then((m) => m.EditClient),
    data: {
      formMode: 'edit',
      layout: {
        activeItem: 'Client Management',
        breadcrumb: 'Client Management > Edit Client',
        title: 'Edit Client',
        subtitle: 'Update client details',
        showToolbar: false,
        backTo: '/client-management',
      },
    },
  },
  {
    path: 'client-management/:clientId',
    providers: [provideClientManagementFeature()],
    loadComponent: () => import('./pages/client-detail/client-detail').then((m) => m.ClientDetail),
    data: {
      formMode: 'view',
      layout: {
        activeItem: 'Client Management',
        breadcrumb: 'Client Management > View Client',
        title: 'View Client',
        subtitle: 'Review client details',
        showToolbar: false,
        backTo: '/client-management',
      },
    },
  },
  {
    path: 'client-management',
    providers: [provideClientManagementFeature()],
    loadComponent: () =>
      import('./pages/client-management-list/client-management-list').then(
        (m) => m.ClientManagementList,
      ),
    data: {
      layout: {
        activeItem: 'Client Management',
        breadcrumb: 'List of Clients',
        title: 'Client Management',
        subtitle: 'Create and manage clients',
        searchPlaceholder: 'Search by Client Name/Email ID/ ID...',
        showFilter: true,
        showMobileFilterOnly: true,
      },
    },
  },
];
